/**
 * The two directions an analytics integration runs in, side by side:
 *
 *   OUT — Trinetra pushes its detections to whatever the customer already watches.
 *   IN  — a third-party model posts its detections to Trinetra and they land on the
 *         same timeline, board and watch-lists as Trinetra's own.
 *
 * Front-end only: the key shown is a placeholder, the test send is simulated.
 */
import { useState } from 'react';
import StatusTag from '../common/StatusTag';
import { copyText } from '../../utils/clipboard';
import { Field, inputClass, monoInputClass } from './ui';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const EVENT_TYPES = [
  { key: 'person_detected', label: 'Person detections' },
  { key: 'vehicle_detected', label: 'Vehicle detections & plates' },
  { key: 'face_match', label: 'Face matches' },
  { key: 'watchlist_hit', label: 'Watch-list hits' },
  { key: 'intrusion', label: 'Zone & line crossings' },
  { key: 'camera_offline', label: 'Camera health changes' },
];

const OUTBOUND_SAMPLE = `{
  "event_id": "9f2c1b7a-4e10-4e2b-9d33-8a4c0f6e51d2",
  "event_type": "watchlist_hit",
  "occurred_at": "2026-09-23T11:42:07.318Z",
  "site": { "node_id": "node-hq-01", "name": "HQ Campus" },
  "camera": { "id": 42, "name": "Gate 1 — Entry", "location": "North Gate" },
  "subject": {
    "kind": "person",
    "confidence": 0.94,
    "attributes": { "upper_wear_colour": "red", "lower_wear_colour": "black" },
    "watchlist": { "category": "wanted", "name": "Subject 118" }
  },
  "media": {
    "snapshot_url": "https://node-hq-01/api/events/9f2c1b7a/snapshot.jpg",
    "clip_url": "https://node-hq-01/api/events/9f2c1b7a/clip.mp4"
  }
}`;

const INBOUND_SAMPLE = `curl -X POST https://node-hq-01/api/integrations/detections \\
  -H "X-Trinetra-Key: tri_live_8c41…f09d" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source": "acme-anpr",
    "camera_ref": "Gate 1 — Entry",
    "detected_at": "2026-09-23T11:42:07Z",
    "type": "vehicle",
    "attributes": { "plate": "MH12AB1234", "class_name": "car" },
    "confidence": 0.97,
    "snapshot_base64": "<jpeg>"
  }'`;

interface Props {
  canEdit: boolean;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => {
        const ok = await copyText(text);
        if (ok) { setDone(true); window.setTimeout(() => setDone(false), 1600); }
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted"
    >
      {done ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
      {done ? 'Copied' : label}
    </button>
  );
}

export default function EventBridge({ canEdit }: Props) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [format, setFormat] = useState('json');
  const [selected, setSelected] = useState<string[]>(['watchlist_hit', 'intrusion']);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [showKey, setShowKey] = useState(false);
  const apiKey = 'tri_live_8c41a2e7b95d4f13a60c7e2b8d5af09d';
  const inboundUrl = `${window.location.origin}/api/integrations/detections`;

  function toggle(key: string) {
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  }

  function sendTest() {
    setSending(true);
    setSent(false);
    window.setTimeout(() => {
      setSending(false);
      setSent(true);
    }, 1100);
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {/* ── Outbound ── */}
      <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
        <div className="flex items-start gap-2">
          <ArrowUpTrayIcon className="mt-0.5 h-5 w-5 text-brand" />
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Send Trinetra events out</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Every detection Trinetra makes can be posted to an existing VMS, SOC platform or
              reporting stack as it happens.
            </p>
          </div>
        </div>

        <Field label="Destination URL" required>
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://soc.customer.local/hooks/trinetra"
            className={monoInputClass}
          />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Signing secret" hint="Sent as an HMAC header so the receiver can trust the payload.">
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
                className={`${inputClass} pr-9`}
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground hover:text-foreground"
                aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              >
                {showSecret ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Payload format">
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className={inputClass}
            >
              <option value="json">Trinetra JSON</option>
              <option value="cef">CEF (SIEM)</option>
              <option value="syslog">Syslog RFC 5424</option>
              <option value="mqtt">MQTT topic</option>
            </select>
          </Field>
        </div>

        <div>
          <p className="mb-2 text-[12px] font-medium text-muted-foreground">Events to forward</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EVENT_TYPES.map((e) => (
              <label key={e.key} className="flex items-center gap-2 text-[12px] text-foreground">
                <input
                  type="checkbox"
                  checked={selected.includes(e.key)}
                  onChange={() => toggle(e.key)}
                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                />
                {e.label}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-background">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[11px] font-medium text-muted-foreground">Sample payload</span>
            <CopyButton text={OUTBOUND_SAMPLE} label="Copy" />
          </div>
          <pre className="max-h-56 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{OUTBOUND_SAMPLE}
          </pre>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={sendTest}
            disabled={!canEdit || !webhookUrl.trim() || sending}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {sending ? (
              <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Sending…</>
            ) : (
              <><PaperAirplaneIcon className="h-4 w-4" /> Send test event</>
            )}
          </button>
          {sent && (
            <span className="flex items-center gap-1.5 text-[12px]" style={{ color: 'var(--success)' }}>
              <CheckCircleIcon className="h-4 w-4" /> Accepted — 200 OK in 142 ms
            </span>
          )}
        </div>
      </div>

      {/* ── Inbound ── */}
      <div className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-card">
        <div className="flex items-start gap-2">
          <ArrowDownTrayIcon className="mt-0.5 h-5 w-5 text-brand" />
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">Accept third-party detections</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              An analytics module the customer already owns can post its results here. They join the
              Trinetra timeline, alert board and watch-lists like any native detection.
            </p>
          </div>
        </div>

        <Field label="Ingest endpoint">
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-background px-3 py-2 font-mono text-[12px] text-foreground">
              {inboundUrl}
            </code>
            <CopyButton text={inboundUrl} label="Copy" />
          </div>
        </Field>

        <Field
          label="API key"
          hint={<>Sent as the <span className="font-mono">X-Trinetra-Key</span> header on every request.</>}
        >
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-background px-3 py-2 font-mono text-[12px] text-foreground">
              {showKey ? apiKey : `${apiKey.slice(0, 12)}${'•'.repeat(18)}`}
            </code>
            <button
              onClick={() => setShowKey((v) => !v)}
              className="rounded-lg border border-border px-2.5 py-1.5 text-muted-foreground transition-colors hover:bg-muted"
              aria-label={showKey ? 'Hide key' : 'Show key'}
            >
              {showKey ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
            <CopyButton text={apiKey} label="Copy" />
          </div>
        </Field>

        <div className="rounded-lg border border-border bg-background">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[11px] font-medium text-muted-foreground">Posting a detection</span>
            <CopyButton text={INBOUND_SAMPLE} label="Copy" />
          </div>
          <pre className="max-h-56 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{INBOUND_SAMPLE}
          </pre>
        </div>

        <div className="border-t border-border pt-3">
          <p className="mb-2 text-[12px] font-medium text-muted-foreground">What a third-party module gets</p>
          <div className="flex flex-wrap gap-1.5">
            <StatusTag tone="info">Camera resolution by name or ID</StatusTag>
            <StatusTag tone="info">Snapshot &amp; clip storage</StatusTag>
            <StatusTag tone="info">Alert rules &amp; severities</StatusTag>
            <StatusTag tone="info">Watch-list matching</StatusTag>
            <StatusTag tone="info">Search &amp; tracking index</StatusTag>
          </div>
        </div>
      </div>
    </div>
  );
}
